import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "emailjs-com";

import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { contactInfo, contactReasons } from "../data/siteData";

const INITIAL_FORM = {
  organisation: "",
  contactName:  "",
  email:        "",
  phone:        "",
  reason:       contactReasons[0],
  message:      "",
};

function Contact() {
  const formRef                   = useRef(null);
  const [form,    setForm]        = useState(INITIAL_FORM);
  const [status,  setStatus]      = useState("idle"); // idle | sending | success | error

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    setStatus("sending");

    emailjs
      .sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setStatus("success");
        setForm(INITIAL_FORM);
      })
      .catch(() => {
        setStatus("error");
      });
  }

  return (
    <div className="page-shell">
      <SiteHeader />

      <main>

        {/* ── 1. PAGE HERO ────────────────────────────────────── */}
        <section className="page-hero contact-hero">
          <div className="container">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span className="breadcrumb-sep">›</span>
              <span className="breadcrumb-current">Contact</span>
            </nav>
            <p className="eyebrow">Get In Touch</p>
            <h1>Contact Us</h1>
            <p className="contact-hero-sub">
              Trade enquiries · Product availability · New accounts · Wholesale support
            </p>
          </div>
          <div className="contact-hero-bg">
            <img
              src="https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=1600&auto=format&fit=crop"
              alt=""
              className="contact-hero-img"
            />
            <div className="contact-hero-overlay" />
          </div>
        </section>

        {/* ── 2. CONTACT GRID ─────────────────────────────────── */}
        <section className="section" style={{ background: "var(--off-white)" }}>
          <div className="container contact-grid">

            {/* Left — info card */}
            <aside className="contact-info-card">
              <p className="eyebrow">Wholesale Enquiries</p>
              <h2>Open a wholesale conversation.</h2>
              <p className="contact-info-body">
                Send wholesale enquiries for pharmacies, clinics, hospitals,
                and distribution buyers that need product availability and
                supply support. Our team responds within 24 hours.
              </p>

              <div className="contact-info-list">

                <div className="contact-info-item">
                  <span className="contact-info-label">Phone</span>
                  <a
                    href={`tel:${contactInfo.phone.replace(/\s/g, "")}`}
                    className="contact-info-value contact-info-link"
                  >
                    {contactInfo.phone}
                  </a>
                </div>

                <div className="contact-info-item">
                  <span className="contact-info-label">Email</span>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="contact-info-value contact-info-link"
                  >
                    {contactInfo.email}
                  </a>
                </div>

                <div className="contact-info-item">
                  <span className="contact-info-label">Address</span>
                  <span className="contact-info-value">{contactInfo.address}</span>
                </div>

                <div className="contact-info-item">
                  <span className="contact-info-label">Hours</span>
                  <span className="contact-info-value">{contactInfo.hours}</span>
                </div>

              </div>

              <div className="contact-info-badges">
                <span className="tag">PCN Licensed</span>
                <span className="tag tag-gold">RC 1727250</span>
              </div>
            </aside>

            {/* Right — form card */}
            <div className="contact-form-card">
              <p className="contact-form-kicker">Quote Request Form</p>
              <h3>Tell us what you need.</h3>

              {status === "success" && (
                <div className="inline-success" role="status">
                  ✓ Your enquiry has been sent. We will get back to you within
                  24 hours.
                </div>
              )}

              {status === "error" && (
                <div className="inline-error" role="alert">
                  Something went wrong. Please try again or email us directly
                  at {contactInfo.email}.
                </div>
              )}

              <form
                ref={formRef}
                className="form-stack contact-form"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="form-group">
                  <label className="form-label" htmlFor="organisation">
                    Organisation
                  </label>
                  <input
                    id="organisation"
                    name="organisation"
                    type="text"
                    required
                    className="form-input"
                    placeholder="Pharmacy, clinic, hospital, or distributor"
                    value={form.organisation}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="contactName">
                    Contact Name
                  </label>
                  <input
                    id="contactName"
                    name="contactName"
                    type="text"
                    required
                    className="form-input"
                    placeholder="Full name"
                    value={form.contactName}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="email">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="form-input"
                      placeholder="procurement@company.com"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="phone">
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      className="form-input"
                      placeholder="+234..."
                      value={form.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="reason">
                    Reason for Enquiry
                  </label>
                  <select
                    id="reason"
                    name="reason"
                    className="form-select"
                    value={form.reason}
                    onChange={handleChange}
                  >
                    {contactReasons.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="message">
                    Product or Order Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    className="form-textarea"
                    placeholder="List products, brands, quantities, or delivery expectations."
                    value={form.message}
                    onChange={handleChange}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary contact-submit-btn"
                  disabled={status === "sending"}
                >
                  {status === "sending" ? "Sending…" : "Send Wholesale Enquiry"}
                </button>
              </form>
            </div>

          </div>
        </section>

        {/* ── 3. MAP PLACEHOLDER ──────────────────────────────── */}
        <section className="section section-sm" style={{ background: "var(--white)" }}>
          <div className="container">
            <div className="section-heading">
              <p className="eyebrow">Our Location</p>
              <h2>Find us in Abuja.</h2>
            </div>
            <div className="contact-map-placeholder">
              <div className="contact-map-inner">
                <div className="contact-map-pin" aria-hidden="true">📍</div>
                <p className="contact-map-address">
                  House 12-14, S. Zakari Close<br />
                  NAF Valley Estate, Asokoro<br />
                  Abuja, Nigeria
                </p>
                <a
                  href="https://maps.google.com/?q=NAF+Valley+Estate+Asokoro+Abuja+Nigeria"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary contact-map-btn"
                >
                  Open in Google Maps →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. BOTTOM CTA ───────────────────────────────────── */}
        <section className="section section-dark">
          <div className="container contact-bottom-grid">
            <div>
              <p className="eyebrow">Not sure where to start?</p>
              <h2>Browse our products first.</h2>
              <p>
                Review our full catalogue of generics, branded medicines, OTC
                lines, and parallel imports before reaching out — so you can
                send us a precise product list.
              </p>
            </div>
            <div className="contact-bottom-actions">
              <Link to="/products" className="btn btn-primary">
                View Products
              </Link>
              <Link to="/services" className="btn btn-outline">
                Our Services →
              </Link>
            </div>
          </div>
        </section>

      </main>

      <SiteFooter />
    </div>
  );
}

export default Contact;
